<script setup lang="ts">
/**
 * Documentation route.
 *
 * An index, not a reader. The documents are Markdown in the repository, published as a book by
 * HonKit and as an API reference by TypeDoc, and neither output is part of this site — so this page
 * links to files that exist rather than rendering copies that would go stale the moment one is
 * edited. See the remarks in `config/documentation.ts` for why the URLs point where they do.
 *
 * The cards and the `ItemList` in the route's structured data come from `documentationConfig`. That
 * is the same discipline the contact route's FAQ follows: a list entity naming documents a visitor
 * cannot see on the page is the claim that earns a manual action, and there is no second copy here
 * to forget.
 *
 * The sections are grouped in the template rather than in configuration. Order and grouping are a
 * presentation decision; the entry's `section` is the fact.
 */

/**
 * Vue
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Libraries
 */
import { ArrowUpRight, BookOpen, Compass, FileCode, Sparkles, Terminal } from '@lucide/vue'

/**
 * Components
 */
import AppBackdrop from '@/components/ui/AppBackdrop.vue'
import AppBadge from '@/components/ui/AppBadge.vue'
import AppButton from '@/components/ui/AppButton.vue'
import AppCard from '@/components/ui/AppCard.vue'
import AppContainer from '@/components/ui/AppContainer.vue'
import AppSectionHeader from '@/components/ui/AppSectionHeader.vue'

/**
 * Config
 */
import { documentationConfig } from '@/config/documentation'
import { softwareConfig } from '@/config/software'

const { t } = useI18n()

/** The section order the page renders in, with the icon each one is introduced by. */
const sections = [
  { key: 'architecture', icon: Compass },
  { key: 'guide', icon: BookOpen },
  { key: 'reference', icon: FileCode }
] as const

/*
 * Grouped once rather than filtered inside the template: a `v-for` calling `filter()` re-runs it on
 * every render, and a section whose configuration holds nothing should not render a heading at all.
 */
const grouped = computed(() =>
  sections
    .map(section => ({
      ...section,
      entries: documentationConfig.entries.filter(entry => entry.section === section.key)
    }))
    .filter(section => section.entries.length > 0)
)

/** The commands that produce the two generated outputs, shown rather than linked — see below. */
const commands = ['npm run build:docs', 'npm run build:docs:book']
</script>

<template>
  <section class="relative overflow-hidden">
    <AppBackdrop />

    <AppContainer class="relative py-20 md:py-24">
      <AppBadge class="enter font-mono uppercase tracking-[0.2em]" style="--enter-step: 0">
        <Sparkles class="h-3.5 w-3.5" aria-hidden="true" />
        {{ t('documentation.eyebrow') }}
      </AppBadge>

      <h1 class="mt-6 max-w-3xl font-headline text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl">
        {{ t('documentation.title') }}
      </h1>

      <p class="enter mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground" style="--enter-step: 1">
        {{ t('documentation.lead') }}
      </p>

      <div class="enter mt-10 flex flex-wrap gap-3" style="--enter-step: 2">
        <AppButton :href="documentationConfig.index">
          <BookOpen class="h-4 w-4" aria-hidden="true" />
          {{ t('documentation.actions.browse') }}
        </AppButton>

        <AppButton variant="outline" :href="softwareConfig.repository">
          <FileCode class="h-4 w-4" aria-hidden="true" />
          {{ t('documentation.actions.source') }}
        </AppButton>
      </div>
    </AppContainer>
  </section>

  <AppContainer
    v-for="(section, index) in grouped"
    :key="section.key"
    as="section"
    :aria-labelledby="`${section.key}-heading`"
    :class="index === grouped.length - 1 ? 'pb-12 pt-4' : 'py-12'"
  >
    <AppSectionHeader
      :id="`${section.key}-heading`"
      :eyebrow="t(`documentation.sections.${section.key}.eyebrow`)"
      :title="t(`documentation.sections.${section.key}.title`)"
      :description="t(`documentation.sections.${section.key}.note`)"
    />

    <!--
      The summary is the page's content, not a teaser for it: a visitor who reads the three points
      has the substance of the document and only follows the button when they want the detail. That
      is also what makes the `abstract` in the JSON-LD honest — it is these exact sentences.

      The button carries the document's title in a visually-hidden span. "Read the document" is a
      perfectly clear label beside the heading it belongs to, and completely useless in the list of
      links a screen-reader user tabs through, where nine of them would read identically.
    -->
    <div class="mt-10 grid gap-5 lg:grid-cols-2">
      <AppCard
        v-for="(entry, position) in section.entries"
        :key="entry.url"
        :title="entry.title"
        :heading-level="3"
        interactive
        class="reveal"
        :style="{ '--reveal-step': position }"
      >
        <template #icon>
          <component :is="section.icon" class="h-5 w-5" aria-hidden="true" />
        </template>

        <p class="text-foreground">{{ entry.description }}</p>

        <ul class="mt-4 space-y-2.5">
          <li v-for="point in entry.summary" :key="point" class="flex gap-2.5">
            <span class="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>{{ point }}</span>
          </li>
        </ul>

        <template #footer>
          <AppButton :href="entry.url" variant="outline" size="sm">
            {{ t('documentation.actions.read') }}
            <span class="sr-only">: {{ entry.title }}</span>
            <ArrowUpRight
              class="h-3.5 w-3.5 shrink-0 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </AppButton>
        </template>
      </AppCard>
    </div>
  </AppContainer>

  <AppContainer as="section" aria-labelledby="generated-heading" class="pb-24 pt-12">
    <AppSectionHeader
      id="generated-heading"
      :eyebrow="t('documentation.generated.eyebrow')"
      :title="t('documentation.generated.title')"
      :description="t('documentation.generated.note')"
    />

    <!--
      Commands rather than links. The API reference and the book are build outputs, not pages this
      site serves, so a link here would either point at a URL that depends on someone having run a
      deploy or claim a route that does not exist. Printing what produces them is the honest version
      of the same information.
    -->
    <ul class="mt-10 grid gap-3 sm:grid-cols-2">
      <li
        v-for="(command, index) in commands"
        :key="command"
        class="reveal flex items-center gap-3 rounded-2xl border border-border bg-card/50 px-5 py-4 backdrop-blur-sm"
        :style="{ '--reveal-step': index }"
      >
        <Terminal class="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <code class="font-code text-sm text-foreground">{{ command }}</code>
      </li>
    </ul>
  </AppContainer>
</template>
